import { useState, useMemo } from 'react';
import { usePopper } from 'react-popper';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import { Popover, PopoverButton, StyledPopoverPanel, PopoverItem, Indicator } from '@crazyegginc/hatch';

import { usePermissions, useAuthContext, useMutation, useModal, useNotifications } from '/src/hooks';
import { deleteFunnelMutation, createFunnelMutation } from '../mutations';
import { getFunnelsQueryParams } from '/src/utils/url';
import { DeleteConfirmationModal } from '/src/components/modals/DeleteConfirmationModal';
import { RenameFunnelModal } from './modals/RenameFunnelModal';

import { FEATURES, ROOT_ELEMENT_ID } from '/src/features/_global/constants';
import { SHARABLE_RESOURCE_TYPES } from '../../team-and-sharing/constants';

import { ReactComponent as CogIcon } from '@crazyegginc/hatch/dist/images/icon-cog-filled.svg';
import { ReactComponent as ShareLinkIcon } from '@crazyegginc/hatch/dist/images/icon-link-outline.svg';
import { ReactComponent as TrashIcon } from '@crazyegginc/hatch/dist/images/icon-remove-filled.svg';
import { ReactComponent as EditIcon } from '@crazyegginc/hatch/dist/images/icon-pencil-filled.svg';
import { ReactComponent as DuplicateIcon } from '@crazyegginc/hatch/dist/images/icon-web-pages-outline.svg';

export function RowActions({ funnel }) {
  const permissions = usePermissions();
  const [referenceElement, setReferenceElement] = useState(null);
  const [tooltipElement, setTooltipElement] = useState(null);

  const { styles, attributes } = usePopper(referenceElement, tooltipElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: 'bottom',
          fallbackPlacements: 'bottom',
        },
      },
      {
        name: 'preventOverflow',
        options: {
          mainAxis: false,
        },
      },
      useMemo(
        () => ({
          name: 'offset',
          options: {
            offset: ({ popper, reference }) => {
              return [-popper.width / 2 + reference.width / 2, -4];
            },
          },
        }),
        [],
      ),
    ],
  });

  const canEdit = permissions.can('edit', FEATURES.FUNNELS).allowed;
  const disableActions = !canEdit;

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <div ref={setReferenceElement}>
            <PopoverButton
              className={classNames('flex h-10 items-center rounded px-4', {
                'border border-mystic-500 bg-white shadow-md': open,
                'cursor-not-allowed disabled:text-cadet-blue-500': disableActions,
                'text-dodger-blue-300': !disableActions,
              })}
              disabled={disableActions}
            >
              <CogIcon aria-label="actions" className="h-[18px] w-[18px] fill-current" />
              <Indicator aria-label="expand" type="dropdown" className="ml-2.5" />
            </PopoverButton>
          </div>

          {open
            ? createPortal(
                <StyledPopoverPanel
                  ref={setTooltipElement}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="w-[250px]"
                  style={{ ...styles.popper }}
                  {...attributes.popper}
                >
                  {() => (
                    <>
                      <div className="absolute -right-px -top-1.25 z-[12] h-2.5 w-[69px] border-r border-mystic-500 bg-white" />
                      <DropdownActions funnel={funnel} />
                    </>
                  )}
                </StyledPopoverPanel>,
                document.querySelector(`#${ROOT_ELEMENT_ID}`),
              )
            : null}
        </>
      )}
    </Popover>
  );
}

export function PopoverActions({ funnel, onDelete = null, onRename }) {
  const permissions = usePermissions();
  const { isSharing } = useAuthContext();
  const [referenceElement, setReferenceElement] = useState(null);
  const [boxElement, setBoxElement] = useState(null);

  const { styles, attributes } = usePopper(referenceElement, boxElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: 'bottom',
          fallbackPlacements: 'bottom',
        },
      },
      {
        name: 'preventOverflow',
        options: {
          mainAxis: false,
        },
      },
      useMemo(
        () => ({
          name: 'offset',
          options: {
            offset: ({ popper, reference }) => {
              return [-popper.width / 2 + reference.width / 2, -4];
            },
          },
        }),
        [],
      ),
    ],
  });

  const canEdit = permissions.can('edit', FEATURES.FUNNELS).allowed;
  const disableActions = !canEdit;

  if (disableActions && isSharing) return null;

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <PopoverButton
            ref={setReferenceElement}
            className={classNames(
              'flex h-[35px] w-[70px] items-center justify-center rounded px-4 text-dodger-blue-300',
              {
                'border border-mystic-500 bg-white shadow-md': open,
              },
            )}
            disabled={disableActions}
          >
            <CogIcon aria-label="actions" className="h-[18px] w-[18px] fill-current" />
            <Indicator aria-label="expand" type="dropdown" className="ml-2.5" />
          </PopoverButton>
          {open ? (
            <StyledPopoverPanel
              ref={setBoxElement}
              align="center"
              static
              className="w-[260px] !py-1 !px-2"
              data-testid="actions-dropdown"
              style={{ ...styles.popper }}
              {...attributes.popper}
            >
              <div className="absolute -right-px -top-1.25 z-[12] h-2.5 w-[69px] border-r border-mystic-500 bg-white" />
              <DropdownActions funnel={funnel} onDelete={onDelete} hideEdit={true} onRename={onRename} />
            </StyledPopoverPanel>
          ) : null}
        </>
      )}
    </Popover>
  );
}

function DropdownActions({ funnel, onDelete = null, hideEdit = false, onRename }) {
  const modal = useModal();
  const deleteFunnel = useMutation(deleteFunnelMutation);
  const createFunnel = useMutation(createFunnelMutation);
  const permissions = usePermissions();
  const notifications = useNotifications();
  const navigate = useNavigate();

  const canEdit = permissions.can('edit', FEATURES.FUNNELS).allowed;

  return (
    <>
      {canEdit && !hideEdit && (
        <Item
          component={Link}
          to={{ pathname: `/funnels/edit/${funnel.id}`, search: getFunnelsQueryParams() }}
          icon={EditIcon}
        >
          Edit this Funnel
        </Item>
      )}

      {canEdit && (
        <Item
          onClick={() => {
            window.SharingModal.show({
              entity: { id: funnel.id, siteId: funnel.siteId, type: SHARABLE_RESOURCE_TYPES.FUNNEL },
            });
          }}
          icon={ShareLinkIcon}
        >
          Get Share Link
        </Item>
      )}

      {canEdit && (
        <Item
          onClick={async () => {
            try {
              await createFunnel.mutateAsync(
                {
                  siteId: funnel.siteId,
                  name: `${funnel.name} copy`,
                  visitStep: funnel.visitStep,
                  goalSteps: funnel.goalSteps.map((s) => s.id),
                },
                {
                  onError: (error) => {
                    notifications.error({
                      content: 'Failed to duplicate funnel.',
                      timeout: 3000,
                      context: { error },
                    });
                  },
                  onSuccess: (data) => {
                    navigate({ pathname: `/funnels/edit/${data.funnelCreate.id}`, search: getFunnelsQueryParams() });
                  },
                },
              );
            } catch {
              //noop
            }
          }}
          icon={DuplicateIcon}
        >
          Duplicate this Funnel
        </Item>
      )}

      {canEdit && (
        <Item
          onClick={() => {
            modal.show(<RenameFunnelModal funnel={funnel} onSuccess={onRename} />);
          }}
          close={true}
          icon={EditIcon}
        >
          Rename this Funnel
        </Item>
      )}

      {canEdit && (
        <Item
          icon={TrashIcon}
          iconClassName="group-hover:!text-carnation-500"
          onClick={() => {
            modal.show(
              <DeleteConfirmationModal
                onDelete={async () => {
                  try {
                    const data = await deleteFunnel.mutateAsync({
                      ids: [funnel.id],
                    });
                    if (data) {
                      modal.close();
                      onDelete?.();
                    }
                  } catch {
                    //noop
                  }
                }}
                text={
                  <>
                    Are you sure you want to delete the Funnel <span className="text-body-1">{funnel.name}</span>?
                  </>
                }
                entity="Funnel"
              />,
            );
          }}
        >
          Delete this Funnel
        </Item>
      )}
    </>
  );
}

function Item({ children, icon: Icon, className, iconClassName, disabled, ...props }) {
  return (
    <PopoverItem
      className={classNames('!flex !select-none !items-center !py-2', className, {
        'hover:!text-body-5 !cursor-not-allowed !bg-white-lilac-500 !italic': disabled,
        'group ': !disabled,
      })}
      disabled={disabled}
      data-testid="action-item"
      {...props}
    >
      {Icon && (
        <Icon
          className={classNames('mr-5 h-4 w-4 fill-current group-hover:text-dodger-blue-500', iconClassName, {
            'text-cadet-blue-500': disabled,
            'text-dodger-blue-300': !disabled,
          })}
        />
      )}
      {children}
    </PopoverItem>
  );
}
